import React, { useState } from 'react';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { X, Plus } from 'lucide-react';

// All 11 transformation types from the ETL Configuration Specification
const TRANSFORMATION_TYPES = [
  {
    type: 'date_format',
    label: 'Date Format',
    description: 'Convert date formats',
    parameters: [
      { name: 'source_format', label: 'Source Format', type: 'text', placeholder: '%m/%d/%Y', required: true },
      { name: 'target_format', label: 'Target Format', type: 'text', placeholder: '%Y-%m-%d', required: true },
    ],
  },
  {
    type: 'value_mapping',
    label: 'Value Mapping',
    description: 'Map values to other values',
    parameters: [
      { name: 'mappings', label: 'Mappings (JSON)', type: 'textarea', placeholder: '{"M": "Male", "F": "Female"}', required: true },
      { name: 'default', label: 'Default Value', type: 'text', placeholder: 'Unknown', required: false },
    ],
  },
  {
    type: 'replace',
    label: 'Replace',
    description: 'Replace text patterns',
    parameters: [
      { name: 'pattern', label: 'Pattern', type: 'text', placeholder: 'NOT FOUND', required: true },
      { name: 'replacement', label: 'Replacement', type: 'text', placeholder: '', required: true },
    ],
  },
  {
    type: 'concat',
    label: 'Concatenate',
    description: 'Concatenate multiple fields',
    parameters: [
      { name: 'fields', label: 'Fields (comma-separated)', type: 'text', placeholder: 'first_name,last_name', required: true },
      { name: 'delimiter', label: 'Delimiter', type: 'text', placeholder: ' ', required: false },
    ],
  },
  {
    type: 'upper',
    label: 'Uppercase',
    description: 'Convert to uppercase',
    parameters: [],
  },
  {
    type: 'lower',
    label: 'Lowercase',
    description: 'Convert to lowercase',
    parameters: [],
  },
  {
    type: 'trim',
    label: 'Trim',
    description: 'Remove whitespace',
    parameters: [],
  },
  {
    type: 'left',
    label: 'Left',
    description: 'Extract left N characters',
    parameters: [
      { name: 'length', label: 'Length', type: 'number', placeholder: '5', required: true },
    ],
  },
  {
    type: 'right',
    label: 'Right',
    description: 'Extract right N characters',
    parameters: [
      { name: 'length', label: 'Length', type: 'number', placeholder: '4', required: true },
    ],
  },
  {
    type: 'substring',
    label: 'Substring',
    description: 'Extract substring by position',
    parameters: [
      { name: 'start', label: 'Start Position', type: 'number', placeholder: '0', required: true },
      { name: 'length', label: 'Length', type: 'number', placeholder: '8', required: true },
    ],
  },
  {
    type: 'cast',
    label: 'Cast',
    description: 'Convert data type',
    parameters: [
      {
        name: 'target_type',
        label: 'Target Type',
        type: 'select',
        options: ['string', 'integer', 'float', 'boolean'],
        required: true,
      },
    ],
  },
];

export default function TransformationSelector({ transformations = [], onChange }) {
  const [showSelector, setShowSelector] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [parameters, setParameters] = useState({});

  const handleSelectType = (transformationType) => {
    setSelectedType(transformationType);
    setParameters({});
  };

  const handleParameterChange = (paramName, value) => {
    setParameters(prev => ({
      ...prev,
      [paramName]: value,
    }));
  };

  const handleAddTransformation = () => {
    if (!selectedType) return;

    const transformation = {
      type: selectedType.type,
    };

    // Add parameters
    selectedType.parameters.forEach(param => {
      let value = parameters[param.name];

      // Parse JSON for mappings
      if (param.name === 'mappings' && value) {
        try {
          value = JSON.parse(value);
        } catch (e) {
          alert('Invalid JSON for mappings');
          return;
        }
      }

      // Parse comma-separated fields for concat
      if (param.name === 'fields' && value) {
        value = value.split(',').map(f => f.trim());
      }

      // Convert to number for numeric parameters
      if (param.type === 'number' && value) {
        value = parseInt(value, 10);
      }

      if (value !== undefined && value !== '') {
        transformation[param.name] = value;
      }
    });

    onChange([...transformations, transformation]);
    setShowSelector(false);
    setSelectedType(null);
    setParameters({});
  };

  const handleRemoveTransformation = (index) => {
    onChange(transformations.filter((_, i) => i !== index));
  };

  const getTransformationLabel = (transform) => {
    const typeInfo = TRANSFORMATION_TYPES.find(t => t.type === transform.type);
    const label = typeInfo?.label || transform.type;

    // Add parameter hints for some types
    if (transform.type === 'date_format') {
      return `${label} (${transform.source_format} → ${transform.target_format})`;
    }
    if (transform.type === 'left' || transform.type === 'right') {
      return `${label} (${transform.length})`;
    }
    if (transform.type === 'cast') {
      return `${label} (${transform.target_type})`;
    }

    return label;
  };

  return (
    <div className="space-y-2">
      {/* Transformation Badges */}
      <div className="flex flex-wrap gap-1">
        {transformations.map((transform, index) => (
          <Badge
            key={index}
            variant="secondary"
            className="cursor-pointer hover:bg-secondary/80"
          >
            {getTransformationLabel(transform)}
            <X
              className="ml-1 h-3 w-3"
              onClick={() => handleRemoveTransformation(index)}
            />
          </Badge>
        ))}
      </div>

      {/* Transformation Selector */}
      {!selectedType ? (
        <button
          onClick={() => setShowSelector(true)}
          className="text-xs text-blue-600 hover:underline flex items-center gap-1"
        >
          <Plus className="h-3 w-3" />
          Add Transform
        </button>
      ) : null}

      {/* Dropdown appears when Add Transform is clicked */}
      {showSelector && !selectedType && (
        <select
          onChange={(e) => {
            const type = TRANSFORMATION_TYPES.find(t => t.type === e.target.value);
            if (type) handleSelectType(type);
          }}
          className="w-full h-9 rounded-md border border-input bg-white px-3 py-2 text-sm"
          defaultValue=""
          autoFocus
        >
          <option value="" disabled>Choose a transformation...</option>
          {TRANSFORMATION_TYPES.map(type => (
            <option key={type.type} value={type.type}>
              {type.label}
            </option>
          ))}
        </select>
      )}

      {/* Configuration Panel */}
      {selectedType && (
        <>
          <div className="text-sm font-medium">
            Configure: {selectedType.label}
          </div>

          {selectedType.parameters.length > 0 ? (
            <div className="space-y-2">
              {selectedType.parameters.map(param => (
                <div key={param.name}>
                  <label className="text-xs font-medium text-muted-foreground">
                    {param.label} {param.required && <span className="text-red-500">*</span>}
                  </label>
                  {param.type === 'select' ? (
                    <select
                      value={parameters[param.name] || ''}
                      onChange={(e) => handleParameterChange(param.name, e.target.value)}
                      className="mt-1 w-full h-8 rounded-md border border-input bg-background px-2 text-sm"
                    >
                      <option value="">Select...</option>
                      {param.options.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : param.type === 'textarea' ? (
                    <textarea
                      value={parameters[param.name] || ''}
                      onChange={(e) => handleParameterChange(param.name, e.target.value)}
                      placeholder={param.placeholder}
                      className="mt-1 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                      rows={3}
                    />
                  ) : (
                    <input
                      type={param.type}
                      value={parameters[param.name] || ''}
                      onChange={(e) => handleParameterChange(param.name, e.target.value)}
                      placeholder={param.placeholder}
                      className="mt-1 w-full h-8 rounded-md border border-input bg-background px-2 text-sm"
                    />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              No parameters required for this transformation.
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedType(null);
                setParameters({});
              }}
            >
              Back
            </Button>
            <Button
              size="sm"
              onClick={handleAddTransformation}
            >
              Add
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
